import { useState } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../lib/api'
import { useUTM, getStoredUTM } from '../hooks/useUTM'

const INCLUDED_ITEMS = [
  'Custom carnivore protocol tailored to your body and goals',
  'Weekly one-on-one check-ins with your dedicated coach',
  'Personalized meal plans and fasting schedules',
  'Direct messaging access for questions between sessions',
  'Progress tracking and protocol adjustments every 2 weeks',
  'Full access to the Library and Mastery Course materials',
]

const QUALIFICATION_CRITERIA = [
  'You have tried other approaches and want something that actually works',
  'You are ready to commit to 90 days of focused transformation',
  'You value expert guidance over guessing on your own',
  'You are willing to invest in your health as a priority',
]

const COMMITMENT_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

const START_OPTIONS = [
  'Immediately',
  'Within 2 weeks',
  'Within a month',
  'Not sure',
]

const INVEST_OPTIONS = [
  'Yes absolutely',
  'I need more info',
  'Not right now',
]

export default function Coaching() {
  useUTM()
  const [activeTab, setActiveTab] = useState<'coaching' | 'mastermind'>('coaching')
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [healthSituation, setHealthSituation] = useState('')
  const [goals, setGoals] = useState('')
  const [commitment, setCommitment] = useState('')
  const [startTimeline, setStartTimeline] = useState('')
  const [readyToInvest, setReadyToInvest] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !firstName) return

    setSubmitting(true)
    setError('')

    try {
      const utm = getStoredUTM()
      const res = await fetch(`${API_URL}/api/coaching/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          email,
          healthSituation,
          goals,
          commitment: commitment ? parseInt(commitment, 10) : undefined,
          startTimeline,
          readyToInvest,
          type: activeTab,
          ...utm,
          visitor_id: localStorage.getItem('fc_visitor_id') || '',
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Something went wrong')
      }

      setSubmitted(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '1rem',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#0f172a',
    marginBottom: '0.5rem',
  }

  return (
    <>
      {/* Hero */}
      <section style={{
        background: '#F2EDE6',
        padding: '6rem 1.5rem 3rem',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: 500,
            color: '#0f172a',
            lineHeight: 1.3,
            marginBottom: '1rem',
          }}>
            90-Day Transformation Coaching
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#555',
            lineHeight: 1.7,
            maxWidth: '560px',
            margin: '0 auto',
          }}>
            Personalized guidance, custom protocols, and expert accountability designed to help you achieve your health goals.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section style={{
        background: '#fff',
        padding: '4rem 1.5rem',
      }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '1.35rem',
            fontWeight: 500,
            color: '#0f172a',
            marginBottom: '2rem',
            textAlign: 'center',
          }}>
            What's Included
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.25rem',
          }}>
            {INCLUDED_ITEMS.map((item, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '1rem 1.25rem',
                  background: '#faf9f7',
                  borderRadius: '5px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <span style={{
                  flexShrink: 0,
                  color: '#16a34a',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                }}>
                  &#10003;
                </span>
                <span style={{
                  fontSize: '0.9rem',
                  color: '#333',
                  lineHeight: 1.5,
                }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualification */}
      <section style={{
        background: '#F2EDE6',
        padding: '4rem 1.5rem',
      }}>
        <div style={{ maxWidth: '580px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '1.35rem',
            fontWeight: 500,
            color: '#0f172a',
            marginBottom: '0.75rem',
            textAlign: 'center',
          }}>
            Is This Right For You?
          </h2>
          <p style={{
            fontSize: '0.95rem',
            color: '#555',
            lineHeight: 1.7,
            textAlign: 'center',
            marginBottom: '2rem',
          }}>
            This program is designed for people who are serious about change. It may be a good fit if:
          </p>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}>
            {QUALIFICATION_CRITERIA.map((item, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  fontSize: '0.95rem',
                  color: '#333',
                  lineHeight: 1.6,
                }}
              >
                <span style={{
                  flexShrink: 0,
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#dc2626',
                  marginTop: '0.55rem',
                }} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Tab Toggle */}
      <section style={{
        background: '#fff',
        padding: '4rem 1.5rem 0',
      }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            gap: '0',
            marginBottom: '2rem',
            borderRadius: '5px',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
          }}>
            <button
              onClick={() => setActiveTab('coaching')}
              style={{
                flex: 1,
                padding: '0.875rem 1rem',
                background: activeTab === 'coaching' ? '#0f172a' : '#fff',
                color: activeTab === 'coaching' ? '#fff' : '#0f172a',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              90-Day Coaching
            </button>
            <button
              onClick={() => setActiveTab('mastermind')}
              style={{
                flex: 1,
                padding: '0.875rem 1rem',
                background: activeTab === 'mastermind' ? '#0f172a' : '#fff',
                color: activeTab === 'mastermind' ? '#fff' : '#0f172a',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer',
                borderLeft: '1px solid #e2e8f0',
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              VIP Mastermind
            </button>
          </div>

          {activeTab === 'mastermind' && (
            <div style={{
              background: '#faf9f7',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              padding: '1.5rem',
              marginBottom: '2rem',
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: 500,
                color: '#0f172a',
                marginBottom: '0.5rem',
              }}>
                VIP Mastermind
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: '#555',
                lineHeight: 1.6,
              }}>
                An exclusive group for high-performers who want accelerated results with direct access to our top coaches, advanced protocols, and a tight-knit accountability circle.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Application Form */}
      <section style={{
        background: '#fff',
        padding: '2rem 1.5rem 4rem',
      }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          {submitted ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 2rem',
              background: '#faf9f7',
              borderRadius: '5px',
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '1.5rem',
                color: '#16a34a',
              }}>
                &#10003;
              </div>
              <h3 style={{
                fontSize: '1.35rem',
                fontWeight: 500,
                color: '#0f172a',
                marginBottom: '0.75rem',
              }}>
                Application Received
              </h3>
              <p style={{
                color: '#555',
                lineHeight: 1.7,
                marginBottom: '2rem',
              }}>
                Thank you for applying. We review every application personally and will be in touch within 48 hours to discuss next steps.
              </p>
              <Link
                to="/pricing"
                style={{
                  color: '#dc2626',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
                &larr; Back to Pricing
              </Link>
            </div>
          ) : (
            <>
              <h2 style={{
                fontSize: '1.35rem',
                fontWeight: 500,
                color: '#0f172a',
                marginBottom: '0.5rem',
                textAlign: 'center',
              }}>
                {activeTab === 'coaching' ? 'Apply for Coaching' : 'Apply for VIP Mastermind'}
              </h2>
              <p style={{
                fontSize: '0.925rem',
                color: '#555',
                textAlign: 'center',
                marginBottom: '2rem',
                lineHeight: 1.6,
              }}>
                Fill out the form below and we will get back to you within 48 hours.
              </p>

              <form onSubmit={handleSubmit} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
              }}>
                <div>
                  <label style={labelStyle}>First Name *</label>
                  <input
                    type="text"
                    placeholder="Your first name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    style={inputStyle}
                    onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                    onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  />
                </div>

                <div>
                  <label style={labelStyle}>What's your current health situation?</label>
                  <textarea
                    placeholder="Tell us about where you are today — diet, energy, challenges, etc."
                    value={healthSituation}
                    onChange={e => setHealthSituation(e.target.value)}
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  />
                </div>

                <div>
                  <label style={labelStyle}>What are your goals for the next 90 days?</label>
                  <textarea
                    placeholder="What would success look like for you?"
                    value={goals}
                    onChange={e => setGoals(e.target.value)}
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  />
                </div>

                <div>
                  <label style={labelStyle}>On a scale of 1-10, how committed are you to making a change?</label>
                  <select
                    value={commitment}
                    onChange={e => setCommitment(e.target.value)}
                    style={{
                      ...inputStyle,
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      paddingRight: '2.5rem',
                    }}
                  >
                    <option value="">Select a number</option>
                    {COMMITMENT_OPTIONS.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>When do you want to start?</label>
                  <select
                    value={startTimeline}
                    onChange={e => setStartTimeline(e.target.value)}
                    style={{
                      ...inputStyle,
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      paddingRight: '2.5rem',
                    }}
                  >
                    <option value="">Select a timeline</option>
                    {START_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Are you ready to invest in yourself?</label>
                  <select
                    value={readyToInvest}
                    onChange={e => setReadyToInvest(e.target.value)}
                    style={{
                      ...inputStyle,
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      paddingRight: '2.5rem',
                    }}
                  >
                    <option value="">Select an answer</option>
                    {INVEST_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '0.975rem 1rem',
                    background: submitting ? '#999' : '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '1rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                    marginTop: '0.5rem',
                  }}
                  onMouseEnter={e => {
                    if (!submitting) (e.currentTarget.style.background = '#b91c1c')
                  }}
                  onMouseLeave={e => {
                    if (!submitting) (e.currentTarget.style.background = '#dc2626')
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>

                {error && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                )}
              </form>

              <p style={{
                fontSize: '0.8rem',
                color: '#999',
                marginTop: '1.25rem',
                textAlign: 'center',
                lineHeight: 1.5,
              }}>
                Results vary. No specific outcomes are guaranteed.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Alternative CTA */}
      <section style={{
        background: '#F2EDE6',
        padding: '3rem 1.5rem',
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: '0.95rem',
          color: '#555',
          marginBottom: '0.75rem',
        }}>
          Not ready for coaching?
        </p>
        <Link
          to="/pricing"
          style={{
            color: '#dc2626',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '0.95rem',
          }}
        >
          Start with the library &rarr;
        </Link>
      </section>
    </>
  )
}
